import type { BoundFormatMoney } from "~/modules/multi-currency/ui/format-money";
import type { ActiveReportArtifact } from "~/utils/types/assistant-report-artifact";

export interface MethodologySection {
  title: "Report" | "Time period" | "Included data" | "Analysis" | "Coverage" | "Published view";
  lines: string[];
}

const SHOWN_TOKEN = "{shown}";
const TOTAL_TOKEN = "{total}";
const POPULATION_TOKEN = "{population}";

/**
 * Fill a capability's coverage sentence with the rows on screen and the matching population.
 * Returns undefined when the sentence asks for a total this report never reported, so the caller
 * can say something true instead of printing a placeholder.
 */
const fillCoverageCopy = (
  copy: string,
  shown: number,
  total: number | undefined,
  populationNoun: string | undefined,
): string | undefined => {
  if (copy.includes(TOTAL_TOKEN) && total === undefined) {
    return undefined;
  }
  return copy
    .replaceAll(SHOWN_TOKEN, shown.toLocaleString())
    .replaceAll(TOTAL_TOKEN, (total ?? shown).toLocaleString())
    .replaceAll(POPULATION_TOKEN, populationNoun ?? "results");
};

/**
 * Neutral coverage caption shared by the results header and the methodology modal.
 * Coverage describes what the user asked for; it is metadata, never a warning.
 *
 * `period` is the report's own date window. Reports that have none (one order and its lines, a
 * current-state order search) must not claim their rows belong to "this period".
 */
export const reportCoverageLine = (
  coverage: ActiveReportArtifact["dataset"]["methodology"]["coverage"],
  coverageCopy?: { bounded: string; partial: string },
  period?: { startDate?: string; endDate?: string },
): string => {
  const inPeriod = period?.startDate !== undefined && period.endDate !== undefined ? " in this period" : "";
  if (
    coverage.populationCount !== undefined &&
    coverage.populationNoun !== undefined &&
    ((!("offset" in coverage) && coverage.state === "complete") || ("offset" in coverage && coverage.offset === 0 && !coverage.truncated))
  ) {
    return `All ${coverage.populationCount.toLocaleString()} ${coverage.populationNoun}${inPeriod} are included.`;
  }
  if (!("offset" in coverage)) {
    if (coverage.state === "bounded") {
      const shown = coverage.returnedRows;
      if (coverageCopy?.bounded === undefined) {
        return `${shown.toLocaleString()} results shown · bounded result`;
      }
      // The finder knows exactly how many population rows matched, so the caption says so rather than
      // leaving "the first 50" hanging against an unnamed population.
      const total = coverage.totalRows ?? coverage.populationCount;
      const boundedLine = fillCoverageCopy(coverageCopy.bounded, shown, total, coverage.populationNoun);
      if (boundedLine === undefined) {
        return `Showing the first ${shown.toLocaleString()} ${coverage.populationNoun ?? "results"}.`;
      }
      if (coverageCopy.bounded.includes(SHOWN_TOKEN) || coverageCopy.bounded.includes(TOTAL_TOKEN)) {
        return boundedLine;
      }
      // When the capability reports its exact population, say what the bounded view is a slice of.
      return coverage.populationCount !== undefined && coverage.populationNoun !== undefined
        ? `${boundedLine} ${coverage.populationCount.toLocaleString()} ${coverage.populationNoun}${inPeriod}.`
        : boundedLine;
    }
    if (coverage.state === "partial") {
      return coverageCopy?.partial ?? `${coverage.returnedRows.toLocaleString()} results shown · partial result`;
    }
    return `${coverage.returnedRows.toLocaleString()} result${coverage.returnedRows === 1 ? "" : "s"}`;
  }
  if (coverage.offset > 0 || coverage.truncated) {
    const pageEnd = coverage.offset + coverage.returnedRows;
    return coverage.returnedRows === 0
      ? `Showing 0 of ${coverage.totalRows.toLocaleString()}`
      : `Showing ${(coverage.offset + 1).toLocaleString()}-${pageEnd.toLocaleString()} of ${coverage.totalRows.toLocaleString()}`;
  }
  return `${coverage.returnedRows.toLocaleString()} result${coverage.returnedRows === 1 ? "" : "s"}`;
};

const fieldLabels = (artifact: ActiveReportArtifact): string[] => {
  const fieldsByKey = new Map(artifact.dataset.fields.map(field => [field.key, field.label]));
  return artifact.presentation.fieldKeys.flatMap(fieldKey => {
    const label = fieldsByKey.get(fieldKey);
    return label ? [label] : [];
  });
};

export const methodologyFooterCopy = (locked: boolean): string =>
  locked
    ? "This calculation has a fixed published order. Export preserves that order and includes all published results."
    : [
        "Changes made directly in the table are temporary.",
        "Check My Work describes the published view;",
        "Export uses the table as currently filtered, sorted, and arranged.",
      ].join(" ");

/**
 * Some capabilities describe their date basis in system terms. Say which date was actually
 * filtered on, in the words a user would use, so Check My Work is honest about the basis.
 */
const dateBasisCopy: Record<string, string> = {
  "Organization-local business dates": "Business dates, in your organization's time zone",
  "Current order state": "Where each order stands right now, not a date range",
  "Current order lifecycle state": "Where this order stands right now, not a date range",
};

export const dateBasisLine = (dateBasis: string): string => dateBasisCopy[dateBasis] ?? `${dateBasis} basis`;

const filterOperatorCopy: Record<string, string> = {
  equals: "is",
  not_equals: "is not",
  lt: "is below",
  lte: "is at most",
  gt: "is above",
  gte: "is at least",
};

export const buildMethodologySections = (artifact: ActiveReportArtifact, formatMoney?: BoundFormatMoney): MethodologySection[] => {
  const methodology = artifact.dataset.methodology;
  const period =
    methodology.startDate && methodology.endDate
      ? `${methodology.startDate} through ${methodology.endDate}`
      : "No date range: this is the current state";
  const publishedFields = fieldLabels(artifact);
  let sortLabel = "No published sort";
  if (artifact.presentation.sort) {
    const direction = artifact.presentation.sort.direction === "asc" ? "ascending" : "descending";
    const sortField = new Map(artifact.dataset.fields.map(field => [field.key, field.label])).get(artifact.presentation.sort.fieldKey);
    const recordLines = artifact.dataset.presentationManifest?.recordLines;
    // In a record, a sort on a line column orders the lines, so name the lines it orders.
    sortLabel =
      artifact.presentation.mode === "record" && recordLines?.fieldKeys.includes(artifact.presentation.sort.fieldKey)
        ? `${recordLines.title} sorted ${direction} by ${sortField ?? "published field"}`
        : `Sorted ${direction} by ${sortField ?? "published field"}`;
  } else if (methodology.governedRanking) {
    const direction = methodology.governedRanking.direction === "asc" ? "ascending" : "descending";
    const sortField = new Map(artifact.dataset.fields.map(field => [field.key, field.label])).get(methodology.governedRanking.fieldKey);
    sortLabel = `Governed order: ${sortField ?? methodology.governedRanking.fieldKey}, ${direction}`;
  }
  const coverageLine = reportCoverageLine(methodology.coverage, artifact.dataset.presentationManifest?.coverageCopy, methodology);
  const labelsByKey = new Map(artifact.dataset.fields.map(field => [field.key, field.label]));
  const analysisLines =
    artifact.dataset.derivedAnalysis?.steps.flatMap(step => {
      const grain = step.lineage.grainKeys.map(key => labelsByKey.get(key) ?? key).join(" × ");
      const populationLines = [
        `${step.lineage.populationLabel}: ${step.lineage.inputRowCount} in, ${step.lineage.outputRowCount} out at ${grain} grain`,
        ...(step.lineage.window
          ? [
              `Primary window: ${step.lineage.window.startDate} through ${step.lineage.window.endDate}`,
              ...(step.lineage.window.comparisonStartDate && step.lineage.window.comparisonEndDate
                ? [`Comparison window: ${step.lineage.window.comparisonStartDate} through ${step.lineage.window.comparisonEndDate}`]
                : []),
            ]
          : []),
      ];
      if (step.operation === "filter_rows") {
        const conditions = step.conditions.map(condition => {
          const label = labelsByKey.get(condition.fieldKey) ?? condition.fieldKey;
          return `${label} ${filterOperatorCopy[condition.operator] ?? condition.operator} ${condition.value}`;
        });
        return [...populationLines, `Filtered to ${conditions.join(" and ")}`];
      }
      if (step.operation === "calendar_run_rate") {
        return [
          ...populationLines,
          `Actual through ${step.asOfCalendarDay}: ${formatMoney?.(step.actualCents) ?? step.actualCents}`,
          `Calendar run-rate projection: ${formatMoney?.(step.projectedCents) ?? step.projectedCents}`,
          `${step.elapsedDays} of ${step.totalDays} calendar days; projection, not forecast`,
        ];
      }
      if (step.operation === "aggregate_by") {
        const targetGrain = step.dimensionKeys.map(key => labelsByKey.get(key) ?? key).join(" × ");
        const measures = step.measureKeys.map(key => labelsByKey.get(key) ?? key).join(", ");
        return [...populationLines, `Aggregated ${measures} to ${targetGrain} grain`];
      }
      if (step.operation === "rank" || step.operation === "top_n") {
        const ranking = methodology.governedRanking;
        const label = labelsByKey.get(ranking?.fieldKey ?? step.measureKey) ?? labelsByKey.get(step.measureKey) ?? step.measureKey;
        const direction = ranking?.direction === "asc" ? "lowest to highest" : "highest to lowest";
        return [
          ...populationLines,
          step.operation === "top_n" ? `Top ${step.n} by ${label}, ${direction}` : `Ranked by ${label}, ${direction}`,
        ];
      }
      if (step.operation === "share_of_total") {
        return [...populationLines, `Share of total calculated from ${labelsByKey.get(step.measureKey) ?? step.measureKey}`];
      }
      if (step.operation === "running_total") {
        return [
          ...populationLines,
          `Running total and cumulative share calculated from ${
            labelsByKey.get(step.measureKey) ?? step.measureKey
          } in the published ranked order`,
        ];
      }
      if (step.operation === "count_by") {
        return [...populationLines, `Counted by ${step.fieldKeys.map(key => labelsByKey.get(key) ?? key).join(" and ")}`];
      }
      const unsupportedStep: never = step;
      return unsupportedStep;
    }) ?? [];
  const analysis: MethodologySection[] =
    analysisLines.length > 0
      ? [
          {
            title: "Analysis",
            lines: analysisLines,
          },
        ]
      : [];
  return [
    { title: "Report", lines: [artifact.presentation.title] },
    { title: "Time period", lines: [period, dateBasisLine(methodology.dateBasis), methodology.timezone] },
    {
      title: "Included data",
      lines: [...methodology.includedData, ...(methodology.statuses.length ? [`Statuses: ${methodology.statuses.join(", ")}`] : [])],
    },
    ...analysis,
    {
      title: "Coverage",
      lines: [coverageLine, ...artifact.dataset.warnings],
    },
    { title: "Published view", lines: [publishedFields.join(", "), sortLabel] },
  ];
};
